import { useState, useEffect } from 'react';
import { useOkapiKy } from '@folio/stripes/core';

const useBulkSafetyCheck = (instanceIds) => {
  const ky = useOkapiKy();
  const [state, setState] = useState({
    loading: false,
    error: null,
    results: [],
    stats: { total: 0, safe: 0, blocked: 0, errors: 0 }
  });

  useEffect(() => {
    if (!instanceIds || instanceIds.length === 0) {
      setState({
        loading: false,
        error: null,
        results: [],
        stats: { total: 0, safe: 0, blocked: 0, errors: 0 }
      });
      return;
    }

    let isCancelled = false;

    const checkInstances = async () => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        // الخطوة 1: جلب جميع الinstances في طلب واحد
        const instanceQuery = instanceIds.map(id => `id=="${id}"`).join(' OR ');
        const instancesResponse = await ky.get(`inventory/instances?query=(${instanceQuery})&limit=${instanceIds.length}`).json();
        const instances = instancesResponse.instances || [];

        if (isCancelled) return;

        // الخطوة 2: جلب جميع Holdings في طلب واحد
        const holdingsQuery = instanceIds.map(id => `instanceId=="${id}"`).join(' OR ');
        const holdingsResponse = await ky.get(`holdings-storage/holdings?query=(${holdingsQuery})&limit=10000`).json();
        const allHoldings = holdingsResponse.holdingsRecords || [];

        if (isCancelled) return;

        // الخطوة 3: جلب جميع Items في طلب واحد (إذا وُجدت holdings)
        let allItems = [];
        if (allHoldings.length > 0) {
          const holdingIds = allHoldings.map(h => h.id);
          const itemsQuery = holdingIds.map(id => `holdingsRecordId=="${id}"`).join(' OR ');
          const itemsResponse = await ky.get(`inventory/items?query=(${itemsQuery})&limit=50000`).json();
          allItems = itemsResponse.items || [];
        }

        if (isCancelled) return;

        // الخطوة 4: تحليل البيانات لكل instance
        const results = instanceIds.map(instanceId => {
          const instance = instances.find(inst => inst.id === instanceId);

          if (!instance) {
            return {
              instanceId,
              instance: null,
              holdingsCount: 0,
              itemsCount: 0,
              unavailableItemsCount: 0,
              canDelete: false,
              status: 'error',
              error: 'Instance not found'
            };
          }

          // البحث عن holdings لهذا الinstance
          const instanceHoldings = allHoldings.filter(h => h.instanceId === instanceId);

          // البحث عن items لهذا الinstance
          const instanceItems = allItems.filter(item => instanceHoldings.some(holding => holding.id === item.holdingsRecordId));

          // فحص حالة المواد
          const unavailableItems = instanceItems.filter(item => {
            const status = item.status?.name || item.status || '';
            return status.toLowerCase() !== 'available';
          });

          return {
            instanceId,
            instance,
            holdingsCount: instanceHoldings.length,
            itemsCount: instanceItems.length,
            unavailableItemsCount: unavailableItems.length,
            canDelete: unavailableItems.length === 0,
            status: 'success'
          };
        });

        // حساب الإحصائيات
        const stats = {
          total: results.length,
          safe: results.filter(r => r.canDelete && r.status === 'success').length,
          blocked: results.filter(r => !r.canDelete && r.status === 'success').length,
          errors: results.filter(r => r.status === 'error').length
        };

        if (!isCancelled) {
          setState({
            loading: false,
            error: null,
            results,
            stats
          });
        }
      } catch (error) {
        if (!isCancelled) {
          setState(prev => ({
            ...prev,
            loading: false,
            error: error.message || 'خطأ في تحليل التسجيلات'
          }));
        }
      }
    };

    checkInstances();

    return () => {
      isCancelled = true;
    };
  }, [instanceIds?.join(','), ky]); // استخدام join للمقارنة

  const refresh = () => {
    if (instanceIds && instanceIds.length > 0) {
      setState(prev => ({ ...prev, loading: true, error: null }));
    }
  };

  return {
    loading: state.loading,
    error: state.error,
    results: state.results,
    stats: state.stats,
    canDeleteAll: state.stats.blocked === 0 && state.stats.errors === 0,
    refresh
  };
};

export default useBulkSafetyCheck;
